import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type AppState, useStore } from '@/store';

/**
 * Supported page types for cursor pagination
 */
type CursorPaginationPageType = 'campaigns' | 'campaignDetails' | 'cases' | 'qc' | 'calls';

/**
 * Configuration options for the cursor pagination hook
 */
interface UseCursorPaginationOptions {
  /**
   * The page type - determines which store state to use
   */
  pageType: CursorPaginationPageType;
  /**
   * Dependencies that should trigger a pagination reset when changed
   * e.g., filters, search terms, workflow ID, etc.
   */
  resetDependencies?: unknown[];
  /**
   * Optional callback when pagination is reset
   */
  onReset?: () => void;
}

/**
 * Return type for the cursor pagination hook
 */
interface UseCursorPaginationReturn {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Map of page numbers to their cursor IDs */
  pageCursors: Record<number, string | undefined>;
  /** Function to handle page changes - call this from onPageChange */
  handlePageChange: (newPage: number, lastItemId: string | undefined) => void;
  /** Get the cursor for the current page (use as start_after_id) */
  getCurrentCursor: () => string | undefined;
  /** Reset pagination to page 1 */
  resetPagination: () => void;
  /** Sync pagination state to URL */
  syncToUrl: (page: number, cursor: string | undefined, shouldReplace?: boolean) => void;
}

/**
 * Custom hook for cursor-based pagination with URL sync and browser history support.
 *
 * This hook provides:
 * - Cursor-based pagination state management via Zustand store
 * - URL synchronization for page and cursor
 * - Browser back/forward navigation support
 * - Automatic reset when dependencies change
 */
export function useCursorPagination(
  options: UseCursorPaginationOptions,
): UseCursorPaginationReturn {
  const { pageType, resetDependencies = [], onReset } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Get store state and actions based on page type
  const store = useStore();

  const { currentPage, pageCursors, setCurrentPage, setPageCursor, resetPaginationStore } =
    getStoreStateForPageType(store, pageType);

  /**
   * Sync pagination state to URL using setSearchParams
   */
  const syncToUrl = useCallback(
    (page: number, cursor: string | undefined, shouldReplace = false) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (page > 1) {
            params.set('page', page.toString());
            if (cursor) {
              params.set('cursor', cursor);
            }
          } else {
            params.delete('page');
            params.delete('cursor');
          }
          return params;
        },
        { replace: shouldReplace },
      );
    },
    [setSearchParams],
  );

  /**
   * Initialize from URL params if store is at page 1 but URL has a different page.
   * This handles direct URL navigation (e.g., user pastes URL with ?page=3&cursor=xxx)
   */
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlCursor = searchParams.get('cursor') || undefined;

    // If URL has page > 1 and store is at page 1, initialize store from URL
    if (urlPage > 1 && currentPage === 1 && urlCursor) {
      setPageCursor(urlPage, urlCursor);
      setCurrentPage(urlPage);
    }
    // Only run on mount - store state persists so we don't need to re-run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sync state from URL when navigating back/forward (popstate events).
   *
   * When the user uses browser back/forward buttons, the URL changes but the component
   * doesn't re-mount. This effect detects URL changes and syncs the store state accordingly.
   *
   * The effect runs when:
   * - searchParams change (browser navigation)
   * - currentPage changes (to ensure we always have the latest store value)
   *
   * The urlPage !== currentPage check prevents unnecessary work when URL and store are in sync
   * (which is the case during normal pagination via next/prev buttons).
   */
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlCursor = searchParams.get('cursor') || undefined;

    // Only sync FROM URL TO STORE when there's a mismatch
    // During normal pagination, handlePageChange updates both URL and store simultaneously,
    // so this condition will be false and the effect does nothing.
    if (urlPage !== currentPage) {
      // Browser back/forward navigation detected - URL changed but store didn't
      if (urlPage === 1 && !urlCursor) {
        // Going back to page 1 (no cursor needed)
        resetPaginationStore();
      } else if (urlPage > 1 && urlCursor) {
        // Going to a specific page with cursor
        setPageCursor(urlPage, urlCursor);
        setCurrentPage(urlPage);
      }
      // else: URL page > 1 but no cursor - intermediate state, skip
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * Reset pagination when dependencies change
   */
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);

    // If both URL and store are at page 1, no need to reset
    if (currentPage === 1 && urlPage === 1) return;

    // Reset pagination when dependencies change
    resetPaginationStore();
    syncToUrl(1, undefined, true);
    onReset?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDependencies);

  /**
   * Handle page change with cursor-based pagination
   */
  const handlePageChange = useCallback(
    (newPage: number, lastItemId: string | undefined) => {
      if (newPage > currentPage) {
        // Going forward - require lastItemId for cursor-based pagination
        if (!lastItemId) {
          return;
        }
        // Store the last item ID as the cursor for the next page
        setPageCursor(newPage, lastItemId);
        setCurrentPage(newPage);
        syncToUrl(newPage, lastItemId, false);
      } else if (newPage < currentPage) {
        // Going backward - we already have the cursor for that page
        setCurrentPage(newPage);
        syncToUrl(newPage, pageCursors[newPage], false);
      }
    },
    [currentPage, pageCursors, syncToUrl, setCurrentPage, setPageCursor],
  );

  /**
   * Get the cursor for the current page
   */
  const getCurrentCursor = useCallback(() => {
    return pageCursors[currentPage];
  }, [pageCursors, currentPage]);

  /**
   * Reset pagination to page 1
   */
  const resetPagination = useCallback(() => {
    resetPaginationStore();
    syncToUrl(1, undefined, true);
  }, [syncToUrl, resetPaginationStore]);

  return {
    currentPage,
    pageCursors,
    handlePageChange,
    getCurrentCursor,
    resetPagination,
    syncToUrl,
  };
}

/**
 * Helper to get the correct store state and actions for a page type
 */
function getStoreStateForPageType(store: AppState, pageType: CursorPaginationPageType) {
  switch (pageType) {
    case 'campaigns':
      return {
        currentPage: store.campaignsCurrentPage,
        pageCursors: store.campaignsPageCursors,
        setCurrentPage: store.setCampaignsCurrentPage,
        setPageCursor: store.setCampaignsPageCursor,
        resetPaginationStore: store.resetCampaignsPagination,
      };
    case 'campaignDetails':
      return {
        currentPage: store.campaignDetailsCurrentPage,
        pageCursors: store.campaignDetailsPageCursors,
        setCurrentPage: store.setCampaignDetailsCurrentPage,
        setPageCursor: store.setCampaignDetailsPageCursor,
        resetPaginationStore: store.resetCampaignDetailsPagination,
      };
    case 'cases':
      return {
        currentPage: store.casesCurrentPage,
        pageCursors: store.casesPageCursors,
        setCurrentPage: store.setCasesCurrentPage,
        setPageCursor: store.setCasesPageCursor,
        resetPaginationStore: store.resetCasesPagination,
      };
    case 'qc':
      return {
        currentPage: store.qcCurrentPage,
        pageCursors: store.qcPageCursors,
        setCurrentPage: store.setQcCurrentPage,
        setPageCursor: store.setQcPageCursor,
        resetPaginationStore: store.resetQcPagination,
      };
    case 'calls':
      return {
        currentPage: store.callsCurrentPage,
        pageCursors: store.callsPageCursors,
        setCurrentPage: store.setCallsCurrentPage,
        setPageCursor: store.setCallsPageCursor,
        resetPaginationStore: store.resetCallsPagination,
      };
  }
}
